import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react"
import { Location, LocationSearchHit } from "@/types/place"
import { Honeybadger } from "@honeybadger-io/react"
import throttle from "lodash/throttle"

import { fitMapToBounds, fitMapToLocation } from "@/lib/gmap-utils"

const DEFAULT_IGNORE_WINDOW_MS = 800
/** Defer window for fitMarkers({ defer: true }) — one render commit. */
const DEFER_AFTER_RENDER_MS = 100

type FittableMarker = {
  latitude?: number | null
  longitude?: number | null
}

export interface FitMarkersOptions {
  /** Defer ~one render commit so batched state updates settle first. */
  defer?: boolean
}

export interface UseMapAutoFitOptions {
  /** Map instance from useMap(id). Null while mounting. */
  map: google.maps.Map | null
  /** Padding for programmatic fits. */
  padding?: google.maps.Padding
  /** Throttle window for fitMarkers (ms). Default 0 = no throttle. */
  throttleMs?: number
  /** Lodash throttle `leading`. Default true. */
  throttleLeading?: boolean
  /** Window for treating boundsChanged as programmatic (ms). Default 800. */
  ignoreWindowMs?: number
}

export interface MapView {
  center: google.maps.LatLngLiteral
  zoom: number
}

export interface UseMapAutoFitResult {
  /** No-op when map is null or markers empty. */
  fitMarkers: (markers: FittableMarker[], options?: FitMarkersOptions) => void
  fitLocation: (location: Location | LocationSearchHit) => Promise<void>
  /** Apply an explicit center/zoom with the same baseline/settle discipline. */
  fitView: (view: MapView) => void
  cancelPendingFit: () => void
  /** Capture current bounds/zoom as the new baseline without fitting. */
  markBaseline: () => void
  /** Clear initialBoundsSet so the next fit's settle becomes the new baseline. */
  resetBaseline: () => void
  /** True during the suppression window after a programmatic fit. */
  ignoringBoundsChanges: boolean
  ignoringBoundsChangesRef: RefObject<boolean>
  /** True after the first programmatic fit's settle window has elapsed. */
  initialBoundsSet: boolean
  initialBoundsSetRef: RefObject<boolean>
  /** Bounds captured at the last programmatic fit. */
  prevBoundsRef: RefObject<google.maps.LatLngBounds | null>
  /** Zoom captured at the last programmatic fit. */
  prevZoomRef: RefObject<number | null>
}

/**
 * Throttled-fit + ignore-window pipeline. Consumers decide *when*
 * to fit; pair with useMapInteractionTracker for user-drag tracking.
 */
export function useMapAutoFit({
  map,
  padding,
  throttleMs = 0,
  throttleLeading = true,
  ignoreWindowMs = DEFAULT_IGNORE_WINDOW_MS,
}: UseMapAutoFitOptions): UseMapAutoFitResult {
  // Refs let throttled closures see latest values without recreating themselves.
  const mapRef = useRef(map)
  mapRef.current = map
  const paddingRef = useRef(padding)
  paddingRef.current = padding
  const ignoreWindowMsRef = useRef(ignoreWindowMs)
  ignoreWindowMsRef.current = ignoreWindowMs

  const prevBoundsRef = useRef<google.maps.LatLngBounds | null>(null)
  const prevZoomRef = useRef<number | null>(null)

  const [ignoringBoundsChanges, setIgnoringBoundsChanges] = useState(false)
  const [initialBoundsSet, setInitialBoundsSet] = useState(false)

  // Mirror state into refs for listener and stable-callback reads.
  const ignoringBoundsChangesRef = useRef(false)
  ignoringBoundsChangesRef.current = ignoringBoundsChanges
  const initialBoundsSetRef = useRef(false)
  initialBoundsSetRef.current = initialBoundsSet

  const ignoreTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const deferTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Latest viewport request wins. fitLocation geocodes before it can fit, so
  // a marker fit (or cancel) that lands mid-flight bumps this and the stale
  // location result is dropped instead of zooming back out over the markers.
  const fitSeqRef = useRef(0)

  const captureBaseline = useCallback(() => {
    const m = mapRef.current
    if (!m) return
    const bounds = m.getBounds()
    if (bounds) prevBoundsRef.current = bounds
    const zoom = m.getZoom()
    if (zoom != null) prevZoomRef.current = zoom
  }, [])

  const startSettleWindow = useCallback(() => {
    setIgnoringBoundsChanges(true)
    ignoringBoundsChangesRef.current = true
    if (ignoreTimeoutRef.current) clearTimeout(ignoreTimeoutRef.current)
    ignoreTimeoutRef.current = setTimeout(() => {
      setIgnoringBoundsChanges(false)
      ignoringBoundsChangesRef.current = false
      setInitialBoundsSet(true)
      initialBoundsSetRef.current = true
      ignoreTimeoutRef.current = null
    }, ignoreWindowMsRef.current)
  }, [])

  const fitMarkersImpl = useCallback(
    (markers: FittableMarker[]) => {
      const m = mapRef.current
      if (!m || markers.length === 0) return
      fitSeqRef.current += 1
      fitMapToBounds({ map: m, markers, padding: paddingRef.current })
      captureBaseline()
      startSettleWindow()
    },
    [captureBaseline, startSettleWindow]
  )

  const throttledFitMarkers = useMemo(() => {
    if (throttleMs <= 0) {
      // No-op cancel/flush for symmetry with the throttled variant.
      return Object.assign(
        (markers: FittableMarker[]) => fitMarkersImpl(markers),
        { cancel: () => {}, flush: () => {} }
      )
    }
    return throttle(
      (markers: FittableMarker[]) => fitMarkersImpl(markers),
      throttleMs,
      { leading: throttleLeading, trailing: true }
    )
  }, [throttleMs, throttleLeading, fitMarkersImpl])

  // Cancel pending throttled call when fn is replaced or on unmount.
  useEffect(() => {
    return () => {
      throttledFitMarkers.cancel()
    }
  }, [throttledFitMarkers])

  useEffect(() => {
    return () => {
      if (ignoreTimeoutRef.current) {
        clearTimeout(ignoreTimeoutRef.current)
        ignoreTimeoutRef.current = null
      }
      if (deferTimeoutRef.current) {
        clearTimeout(deferTimeoutRef.current)
        deferTimeoutRef.current = null
      }
    }
  }, [])

  const fitMarkers = useCallback(
    (markers: FittableMarker[], options?: FitMarkersOptions) => {
      if (deferTimeoutRef.current) {
        clearTimeout(deferTimeoutRef.current)
        deferTimeoutRef.current = null
      }
      if (options?.defer) {
        deferTimeoutRef.current = setTimeout(() => {
          deferTimeoutRef.current = null
          throttledFitMarkers(markers)
        }, DEFER_AFTER_RENDER_MS)
      } else {
        throttledFitMarkers(markers)
      }
    },
    [throttledFitMarkers]
  )

  const fitLocation = useCallback(
    async (location: Location | LocationSearchHit) => {
      const m = mapRef.current
      if (!m) return
      const seq = ++fitSeqRef.current
      const isCurrent = () => seq === fitSeqRef.current
      // Suppress up front: geocode + fitBounds bounds events are programmatic.
      setIgnoringBoundsChanges(true)
      ignoringBoundsChangesRef.current = true
      if (ignoreTimeoutRef.current) {
        clearTimeout(ignoreTimeoutRef.current)
        ignoreTimeoutRef.current = null
      }
      try {
        await fitMapToLocation({
          map: m,
          location,
          padding: paddingRef.current,
          shouldApply: isCurrent,
        })
      } catch (error) {
        Honeybadger.notify(error instanceof Error ? error : String(error), {
          context: {
            component: "useMapAutoFit",
            action: "fitLocation",
          },
        })
      } finally {
        // Superseded: whoever took over already owns the baseline and
        // settle window (or cleared suppression); don't reopen it.
        if (isCurrent()) {
          captureBaseline()
          startSettleWindow()
        }
      }
    },
    [captureBaseline, startSettleWindow]
  )

  const fitView = useCallback(
    (view: MapView) => {
      const m = mapRef.current
      if (!m) return
      fitSeqRef.current += 1
      m.setCenter(view.center)
      m.setZoom(view.zoom)
      captureBaseline()
      startSettleWindow()
    },
    [captureBaseline, startSettleWindow]
  )

  const cancelPendingFit = useCallback(() => {
    fitSeqRef.current += 1
    throttledFitMarkers.cancel()
    if (deferTimeoutRef.current) {
      clearTimeout(deferTimeoutRef.current)
      deferTimeoutRef.current = null
    }
    if (ignoreTimeoutRef.current) {
      clearTimeout(ignoreTimeoutRef.current)
      ignoreTimeoutRef.current = null
    }
    setIgnoringBoundsChanges(false)
    ignoringBoundsChangesRef.current = false
  }, [throttledFitMarkers])

  const markBaseline = useCallback(() => {
    captureBaseline()
    setInitialBoundsSet(true)
    initialBoundsSetRef.current = true
  }, [captureBaseline])

  const resetBaseline = useCallback(() => {
    setInitialBoundsSet(false)
    initialBoundsSetRef.current = false
  }, [])

  return {
    fitMarkers,
    fitLocation,
    fitView,
    cancelPendingFit,
    markBaseline,
    resetBaseline,
    ignoringBoundsChanges,
    ignoringBoundsChangesRef,
    initialBoundsSet,
    initialBoundsSetRef,
    prevBoundsRef,
    prevZoomRef,
  }
}
